import type {
  LoginCredentials,
  LoginResponse,
  LoginErrorResponse,
  PasswordResetResponse,
} from './types';

export class LoginError extends Error {
  status: number;
  errorCode?: string;
  resetToken?: string;
  info?: LoginErrorResponse['info'];

  constructor(
    message: string,
    status: number,
    info?: LoginErrorResponse['info'],
    errorCode?: string,
    resetToken?: string,
  ) {
    super(message);
    this.name = 'LoginError';
    this.status = status;
    this.info = info;
    this.errorCode = errorCode;
    this.resetToken = resetToken;
  }
}

/**
 * Authenticate user with email/password against the V4 (PostgreSQL-only)
 * login endpoint. The session that gets created is V4-only and lives at
 * `req.session.v4` on the server — legacy v2/v3 routes that read
 * `req.session.user` are not affected.
 *
 * POST /api/v4/auth/login with { username, password, rememberMe, recaptchaToken }
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  let response: Response;

  try {
    response = await fetch('/api/v4/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        username: credentials.username,
        password: credentials.password,
        rememberMe: credentials.rememberMe ?? false,
        recaptchaToken: credentials.recaptchaToken,
      }),
    });
  } catch {
    // Network error (offline, DNS failure, CORS, etc.)
    throw new LoginError('Unable to connect to the server', 0);
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    // Server returned non-JSON (e.g., nginx error page, HTML)
    throw new LoginError('Server error. Please try again.', response.status);
  }

  if (!response.ok) {
    const errorData = data as LoginErrorResponse;
    throw new LoginError(
      errorData.msg || errorData.message || 'Login failed',
      response.status,
      errorData.info,
      errorData.error,
      errorData.resetToken,
    );
  }

  return data as LoginResponse;
}

export class PasswordResetError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PasswordResetError';
    this.status = status;
  }
}

async function requestAuthJson<T>(url: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });
  } catch {
    throw new PasswordResetError('Unable to connect to the server', 0);
  }

  let data: unknown = {};
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new PasswordResetError('Server error. Please try again.', response.status);
    }
  }

  if (!response.ok) {
    const message = typeof data === 'object' && data && 'message' in data
      ? String((data as { message?: string }).message)
      : 'Password reset failed';
    throw new PasswordResetError(message, response.status);
  }

  return data as T;
}

export async function requestPasswordReset(email: string): Promise<PasswordResetResponse> {
  return requestAuthJson<PasswordResetResponse>('/api/v4/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function validatePasswordResetToken(token: string): Promise<PasswordResetResponse> {
  return requestAuthJson<PasswordResetResponse>(
    `/api/v4/auth/password-reset/${encodeURIComponent(token)}`,
    { method: 'GET' }
  );
}

export async function resetPassword(token: string, password: string): Promise<PasswordResetResponse> {
  return requestAuthJson<PasswordResetResponse>(
    `/api/v4/auth/password-reset/${encodeURIComponent(token)}`,
    {
      method: 'POST',
      body: JSON.stringify({ password }),
    }
  );
}
